import { forwardRef, ReactNode } from "react";
import classNames from "classnames";

interface ButtonProps {
  type?: "button" | "submit";
  variant?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "link"
    | "inline-link"
    | "outline-primary"
    | "outline-secondary"
    | "danger"
    | "gray-underline"
    | "custom"
    | "none"
    | "outline-gray";
  className?: string;
  onClick?: () => void;
  children: ReactNode | ReactNode[];
  disabled?: boolean;
  preIcon?: ReactNode;
  postIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = "button",
      variant = "primary",
      className,
      onClick,
      children,
      disabled = false,
      preIcon,
      postIcon,
      ...rest
    }: ButtonProps,
    ref
  ) => {
    let color;
    switch (variant) {
    case "secondary":
      color =
          "bg-secondary text-secondary-contrast border-2 border-secondary";
      break;
    case "tertiary":
      color = "bg-tertiary text-tertiary-contrast border-2 border-tertiary";
      break;
    case "link":
    case "inline-link":
      color = "bg-transparent text-primary";
      break;
    case "none":
      color = "bg-transparent border border-transparent focus:outline-none";
      break;
    case "danger":
      color = "bg-danger text-white border-2 border-danger";
      break;
    case "outline-primary":
      color = "bg-white text-primary border border-primary";
      break;
    case "outline-secondary":
      color = "bg-white text-secondary border-2 border-secondary";
      break;
    case "gray-underline":
      color = "bg-transparent text-gray-500 underline";
      break;
    case "custom":
      color = "border-2";
      break;
    case "outline-gray":
      color = "bg-gray-100 text-gray-500 border border-gray-500";
      break;
    default:
      color = "bg-primary text-primary-contrast border-2 border-primary";
      break;
    }

    return (
      <button
        type={type}
        ref={ref}
        onClick={onClick}
        disabled={disabled}
        className={classNames(
          className,
          "rounded appearance-none h-10".concat(
            " ",
            variant === "inline-link" ? "inline-block" : "px-4"
          ),
          preIcon || postIcon ? "flex items-center" : "",
          disabled ? "opacity-60" : "",
          color
        )}
        {...rest}
      >
        {preIcon}
        {children}
        {postIcon}
      </button>
    );
  }
);

Button.displayName = "Button";

Button.defaultProps = {
  type: "button",
  variant: "primary",
};
